import { combineEpics, Epic } from 'redux-observable';
import { from, of } from 'rxjs';
import {
  switchMap,
  map,
  startWith,
  catchError,
  filter
} from 'rxjs/operators';
import { isOfType } from 'typesafe-actions';
import { Position, Toaster, Intent } from '@blueprintjs/core';

import {
  FolderActions,
  FolderActionTypes,
  loadingFolders,
  loadedFolders,
  loadingFoldersFailed,
  createdFolder,
  creatingFolder,
  creatingFolderFailed,
  updatedFolder,
  updatingFolder,
  updatingFolderFailed,
  deletedFolder,
  deletingFolder,
  deletingFolderFailed,
} from '../actions/folderActions';
import { IState } from '../reducers';
import {
  getFolders,
  createFolder,
  updateFolder,
  deleteFolder,
} from '../../services/folderApi';

export const folderToaster = Toaster.create({
  className: 'folder-toaster',
  position: Position.TOP
});

const loadFoldersEpic: Epic<FolderActions, FolderActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(FolderActionTypes.LOAD_FOLDERS)),
    switchMap((action: any) =>
      from(getFolders()).pipe(
        map((response: any) => loadedFolders(response.result)),
        startWith(loadingFolders()),
        catchError((error: any) => {
          return of(loadingFoldersFailed());
        })
      )
    )
  );

const createFolderEpic: Epic<FolderActions, FolderActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(FolderActionTypes.CREATE_FOLDER)),
    switchMap((action: any) =>
      from(createFolder(action.payload)).pipe(
        map((response: any) => createdFolder(response.result)),
        startWith(creatingFolder()),
        catchError((error: any) => {
          let message = error.response?.data?.message || "";

          if (typeof message === 'object') {
            /*
             * {field: [error], ...}
             */
            const key = Object.keys(message)[0];
            const keyError = message[key][0];

            message = `Field '${key}': ${keyError}`;
          }

          folderToaster.show({
            message: `An error occurred while creating the folder: ${message}`,
            intent: Intent.DANGER,
          });

          return of(creatingFolderFailed());
        })
      )
    )
  );

const updateFolderEpic: Epic<FolderActions, FolderActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(FolderActionTypes.UPDATE_FOLDER)),
    switchMap((action: any) =>
      from(updateFolder(action.payload.uid as string, action.payload.folder)).pipe(
        map((response: any) => updatedFolder({
          ...response.result,
          uid: action.payload.uid
        })),
        startWith(updatingFolder()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          folderToaster.show({
            message: `An error occurred while updating: ${message}.`,
            intent: Intent.DANGER
          });

          return of(updatingFolderFailed());
        })
      )
    )
  );

const deleteFolderEpic: Epic<FolderActions, FolderActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(FolderActionTypes.DELETE_FOLDER)),
    switchMap((action: any) =>
      from(deleteFolder(action.payload)).pipe(
        map((response: any) => {
          if (response?.message === 'OK') {
            folderToaster.show({
              message: `Folder successfully deleted.`,
              intent: Intent.SUCCESS
            });
          }
          return deletedFolder(action.payload);
        }),
        startWith(deletingFolder()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          folderToaster.show({
            message: `An error occurred while deleting folder: ${message}.`,
            intent: Intent.DANGER
          });

          return of(deletingFolderFailed());
        })
      )
    )
  );

export default combineEpics(
  loadFoldersEpic,
  createFolderEpic,
  updateFolderEpic,
  deleteFolderEpic,
);
